import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Empty, Spin, Switch } from "antd";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  PaperClipOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { OrderSteps } from "../../components/OrderSteps";
import type { OrderStepItem } from "../../components/OrderSteps";
import {
  getAttachmentConfig,
  getSignatureConfig,
  useCountryStore,
  useMetadataStore,
  useConfigStore,
  downloadOrderFormPdf,
  getFileBlob,
  OrderStatus,
} from "@ifa/utils";
import type { OrderAttachmentConfig, SignatureConfigItem } from "@ifa/utils";
import CustomButton from "../../components/CustomButton";
import RiskAssessmentView from "../OrderForms/RiskAssessmentView";
import { PersonView } from "./components/PersonView";
import { BeneficiaryView } from "./components/BeneficiaryView";
import { BeneficiaryDetailModal } from "./components/BeneficiaryDetailModal";
import { ProductView } from "./components/ProductView";
import { AttachmentsView } from "./components/AttachmentsView";
import { SignDocumentView } from "./components/SignDocumentView";
import { SignInfoView } from "./components/SignInfoView";
import { FinancialView } from "./components/FinancialView";
import { useIOSConfirm } from "../../hooks";
import styles from "./index.module.css";
import { isZuuFnaVariant } from "../order-fna/fnaVariant.js";
import {
  cacheOrderPageStatus,
  getOrderDetailForPage,
  readCachedOrderPageStatus,
} from "../../services/orderDetail";
import {
  deriveOrderDetailFinancialSubStep,
  deriveOrderDetailTopLogicalStep,
  shouldShowCraStep,
} from "../orderProgress.js";
import { getRequestedOrderStep } from "../orderFormStepUtils";

export function OrderDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  const locationState = (location.state || {}) as Record<string, any>;
  const formAreaRef = useRef<HTMLDivElement>(null);
  const pendingConfirmationNoticeShownRef = useRef(false);
  const hasManuallyChangedTopStepRef = useRef(false);
  const hasAutoResolvedFinancialStepRef = useRef(false);
  const { confirm } = useIOSConfirm();

  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState<any>({});
  const [currentTopStep, setCurrentTopStep] = useState(0);
  const [restoredTopStep, setRestoredTopStep] = useState<number | null>(null);
  const [currentSidebarStep, setCurrentSidebarStep] = useState("policyholder");
  const [activeProductKey, setActiveProductKey] = useState("0");
  const { allCountries, fetchCountryList } = useCountryStore();
  const { formRequiredConfig } = useMetadataStore();
  const brandId = useConfigStore((state) => state.brandId);
  const tenantCode = useConfigStore((state) => state.tenantProfile?.tenantCode);
  const isZuuMode = isZuuFnaVariant({ brandId, tenantCode });

  useEffect(() => {
    fetchCountryList();
  }, []);

  const getCountryName = (code?: string) => {
    if (!code) return "N/A";
    const country = allCountries.find((c) => c.countryCode === code);
    return country?.cname || country?.name || code;
  };

  const [currentFinancialStep, setCurrentFinancialStep] = useState<
    "financial-info" | "fna"
  >("financial-info");

  // Attachment State
  const [attachmentConfig, setAttachmentConfig] = useState<
    OrderAttachmentConfig[]
  >([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [selectedAttachmentCategory, setSelectedAttachmentCategory] =
    useState<string>("");

  // Signature State
  const [signatureConfig, setSignatureConfig] = useState<SignatureConfigItem[]>(
    [],
  );
  const [activeDocKey, setActiveDocKey] = useState<string>("");

  // Preview Modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
  }>();

  // Beneficiary Detail Modal state
  const [beneficiaryDetailOpen, setBeneficiaryDetailOpen] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<any>(null);

  // Signature Info Preview Modal states
  const [signPhotoPreviewOpen, setSignPhotoPreviewOpen] = useState(false);
  const [signPhotoPreviewFile, setSignPhotoPreviewFile] = useState<{
    url: string;
    name: string;
  }>();
  const [signatureImagePreviewOpen, setSignatureImagePreviewOpen] =
    useState(false);
  const [signatureImagePreviewFile, setSignatureImagePreviewFile] = useState<{
    url: string;
    name: string;
  }>();

  // Signature Info Display URLs (converted from OSS paths)
  const [signPhotoDisplayUrl, setSignPhotoDisplayUrl] = useState<string>("");
  const [signatureDisplayUrls, setSignatureDisplayUrls] = useState<
    Record<number, string>
  >({});

  // CRA loading state
  const [craLoading, setCraLoading] = useState(false);
  const cachedPageStatus = orderId ? readCachedOrderPageStatus(orderId) : {};
  const pageOrderStatus =
    orderData?.orderStatus ??
    locationState.orderStatus ??
    cachedPageStatus.orderStatus;
  const pageOrderAuditStatus =
    orderData?.orderAuditStatus ??
    locationState.orderAuditStatus ??
    cachedPageStatus.orderAuditStatus;

  // Restore state from session storage
  useEffect(() => {
    if (orderId && (locationState.orderStatus || locationState.orderAuditStatus)) {
      cacheOrderPageStatus(orderId, {
        orderStatus: locationState.orderStatus,
        orderAuditStatus: locationState.orderAuditStatus,
      });
    }
  }, [orderId, locationState.orderAuditStatus, locationState.orderStatus]);

  // Restore state from session storage
  useEffect(() => {
    if (orderId) {
      const key = `order_detail_step_${orderId}`;
      const saved = sessionStorage.getItem(key);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.topStep !== undefined) {
            setCurrentTopStep(parsed.topStep);
            setRestoredTopStep(parsed.topStep);
          } else {
            setRestoredTopStep(null);
          }
          if (parsed.sidebarStep) setCurrentSidebarStep(parsed.sidebarStep);
        } catch (e) {
          console.error("Failed to restore step state", e);
          setRestoredTopStep(null);
        }
      } else {
        setRestoredTopStep(null);
      }
    }
  }, [orderId]);

  // Save state to session storage
  useEffect(() => {
    if (orderId) {
      const key = `order_detail_step_${orderId}`;
      const stateToSave = {
        topStep: currentTopStep,
        sidebarStep: currentSidebarStep,
      };
      sessionStorage.setItem(key, JSON.stringify(stateToSave));
    }
  }, [orderId, currentTopStep, currentSidebarStep]);

  // Fetch Order Data
  useEffect(() => {
    if (orderId) {
      setLoading(true);
      getOrderDetailForPage(orderId, {
        mode: "view",
        orderStatus: pageOrderStatus,
        orderAuditStatus: pageOrderAuditStatus,
      })
        .then((res) => {
          setOrderData(res);
          cacheOrderPageStatus(orderId, {
            orderStatus: res.orderStatus,
            orderAuditStatus: res.orderAuditStatus,
          });

        })
        .catch((err) => {
          console.error("Failed to fetch order detail:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [orderId, pageOrderAuditStatus, pageOrderStatus]);

  useEffect(() => {
    if (
      orderData?.orderStatus === OrderStatus.PendingConfirmation &&
      !pendingConfirmationNoticeShownRef.current
    ) {
      pendingConfirmationNoticeShownRef.current = true;
      confirm({
        title: "提示",
        content:
          "由於本訂單涉及交叉銷售安排，現正進行合規審核程序。\n於審核完成前，您只可查閱本訂單的部分內容，其他操作將暫時受限。",
        okText: "確認",
        showCancel: false,
      });
    }
  }, [orderData?.orderStatus, confirm]);

  // Fetch Attachment Config
  useEffect(() => {
    if (orderId) {
      setLoadingAttachments(true);
      getAttachmentConfig(Number(orderId))
        .then((config) => {
          setAttachmentConfig(config || []);
          if (config && config.length > 0) {
            setSelectedAttachmentCategory(config[0].type || "");
          }
        })
        .catch((err) => {
          console.error("Failed to fetch attachment config:", err);
        })
        .finally(() => {
          setLoadingAttachments(false);
        });
    }
  }, [orderId]);

  // Fetch Signature Config - 只在 attachmentCompleted 为 true 时调用
  useEffect(() => {
    if (orderId && orderData.attachmentCompleted) {
      getSignatureConfig(Number(orderId))
        .then(async (res) => {
          if (res && res.configs) {
            // Process files to get signed URLs
            const processedConfig = await Promise.all(
              res.configs.map(async (doc: SignatureConfigItem) => {
                let fileUrl = "";
                if (doc.formType) {
                  try {
                    const pdfResult = await downloadOrderFormPdf({
                      orderId: Number(orderId),
                      formType: doc.formType,
                    });
                    if (pdfResult && pdfResult.blob) {
                      fileUrl = URL.createObjectURL(pdfResult.blob);
                    }
                  } catch (e) {
                    console.error(
                      `Failed to download PDF for ${doc.formType}`,
                      e,
                    );
                  }
                }
                return {
                  ...doc,
                  key: doc.key || doc.formType || doc.type,
                  _fileUrl: fileUrl,
                };
              }),
            );

            setSignatureConfig(processedConfig);
            if (processedConfig.length > 0) {
              const first = processedConfig[0];
              setActiveDocKey(first.key || first.formType || first.type || "");
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch signature config:", err);
        });
    }
  }, [orderId, orderData.attachmentCompleted]);

  // Convert OSS paths to display URLs for signature info - 只在 attachmentCompleted 为 true 时调用
  useEffect(() => {
    const convertSignatureUrls = async () => {
      if (!orderData?.signInfo || !orderData.attachmentCompleted) return;

      // Convert photo URL
      if (
        orderData.signInfo.photo &&
        orderData.signInfo.photo.startsWith("oss://")
      ) {
        try {
          const { url } = await getFileBlob(orderData.signInfo.photo);
          setSignPhotoDisplayUrl(url);
        } catch (error) {
          console.error("Failed to convert photo URL:", error);
        }
      } else if (orderData.signInfo.photo) {
        setSignPhotoDisplayUrl(orderData.signInfo.photo);
      }

      // Convert signature URLs
      if (
        orderData.signInfo.signatures &&
        Array.isArray(orderData.signInfo.signatures)
      ) {
        const urlMap: Record<number, string> = {};
        await Promise.all(
          orderData.signInfo.signatures.map(async (sig: any, index: number) => {
            if (sig.signature && sig.signature.startsWith("oss://")) {
              try {
                const { url } = await getFileBlob(sig.signature);
                urlMap[index] = url;
              } catch (error) {
                console.error(
                  `Failed to convert signature URL ${index}:`,
                  error,
                );
              }
            } else if (sig.signature) {
              urlMap[index] = sig.signature;
            }
          }),
        );
        setSignatureDisplayUrls(urlMap);
      }
    };

    convertSignatureUrls();
  }, [orderData?.signInfo, orderData?.attachmentCompleted]);

  // 根据订单完成状态计算可见步骤
  const visibleSteps = useMemo(() => {
    // 前4步始终显示
    const steps: OrderStepItem[] = [
      {
        title: "訂單資料",
        icon: <FileTextOutlined />,
      },
      {
        title: isZuuMode ? "財務資料" : "財務需求分析",
        icon: <BankOutlined />,
      },
      {
        title: "產品投保",
        icon: <SafetyCertificateOutlined />,
      },
      {
        title: "訂單附件",
        icon: <PaperClipOutlined />,
      },
    ];

    // 簽署檔案始终显示
    steps.push({
      title: "簽署檔案",
      icon: <FormOutlined />,
    });

    // 只有当订单状态为 ORDER_AUDIT, COMPLIANCE_AUDIT, COMPLETED 时才显示 CRA 步骤
    const showCRA = shouldShowCraStep(orderData.orderStatus);

    if (showCRA) {
      steps.push({
        title: "CRA",
        icon: <CheckCircleOutlined />,
      });
    }

    return steps;
  }, [
    isZuuMode,
    orderData.orderStatus,
  ]);

  // 计算实际步骤索引映射（前5步固定，CRA按条件）
  const stepIndexMap = useMemo(() => {
    const map: Record<number, number> = {
      0: 0, // 订单资料
      1: 1, // 财务需求分析
      2: 2, // 产品投保
      3: 3, // 订单附件
      4: 4, // 签署文件
    };

    const showCRA = shouldShowCraStep(orderData.orderStatus);

    if (showCRA) {
      map[5] = 5; // CRA
    }

    return map;
  }, [
    orderData.orderStatus,
  ]);

  // 反向映射：从可见步骤索引到逻辑步骤索引
  const visibleToLogicalMap = useMemo(() => {
    const reverseMap: Record<number, number> = {};
    Object.entries(stepIndexMap).forEach(([logical, visible]) => {
      reverseMap[Number(visible)] = Number(logical);
    });
    return reverseMap;
  }, [stepIndexMap]);

  // 获取当前逻辑步骤（用于渲染内容）
  const currentLogicalStep = useMemo(() => {
    return visibleToLogicalMap[currentTopStep] ?? 0;
  }, [currentTopStep, visibleToLogicalMap]);

  useEffect(() => {
    if (!isZuuMode) return;
    if (!orderId || !orderData) return;
    if (hasManuallyChangedTopStepRef.current) return;

    const requestedStep = getRequestedOrderStep({
      urlInitialStep: new URLSearchParams(location.search).get("initialStep"),
      stateInitialStep: locationState.initialStep,
      cachedTopStep: restoredTopStep,
      ignoreZeroStateInitialStep: true,
    });
    const autoLogicalStep = deriveOrderDetailTopLogicalStep(orderData);
    const targetLogicalStep =
      requestedStep !== null
        ? Math.min(requestedStep, autoLogicalStep)
        : autoLogicalStep;
    const targetVisibleStep = stepIndexMap[targetLogicalStep] ?? 0;

    if (targetVisibleStep !== currentTopStep) {
      setCurrentTopStep(targetVisibleStep);
    }
  }, [
    currentTopStep,
    isZuuMode,
    location.search,
    locationState.initialStep,
    orderData,
    orderId,
    restoredTopStep,
    stepIndexMap,
  ]);

  useEffect(() => {
    if (!isZuuMode) {
      hasAutoResolvedFinancialStepRef.current = false;
      return;
    }

    if (currentLogicalStep !== 1) {
      hasAutoResolvedFinancialStepRef.current = false;
      return;
    }

    if (!orderData || hasAutoResolvedFinancialStepRef.current) {
      return;
    }

    const nextFinancialStep = deriveOrderDetailFinancialSubStep(orderData);
    hasAutoResolvedFinancialStepRef.current = true;

    if (nextFinancialStep !== currentFinancialStep) {
      setCurrentFinancialStep(nextFinancialStep);
    }
  }, [currentFinancialStep, currentLogicalStep, isZuuMode, orderData]);

  const handleTopStepClick = (nextVisibleStep: number) => {
    hasManuallyChangedTopStepRef.current = true;
    setCurrentTopStep(nextVisibleStep);
  };

  const handleFinancialStepClick = (nextStep: "financial-info" | "fna") => {
    hasAutoResolvedFinancialStepRef.current = true;
    setCurrentFinancialStep(nextStep);
  };

  // Update step statuses based on order status or data completeness
  const updatedTopSteps = visibleSteps.map((step, index) => ({
    ...step,
    status: index === currentTopStep ? "process" : "finish",
    onClick: () => handleTopStepClick(index),
  }));

  const sidebarItems = useMemo(() => {
    const items = [
      {
        key: "policyholder",
        label: "投保人",
        icon: <SafetyCertificateOutlined />,
        status: "finish",
      },
    ];

    if (
      orderData.secondPolicyholder &&
      Object.keys(orderData.secondPolicyholder).length > 0
    ) {
      items.push({
        key: "policyholder2",
        label: "第二投保人",
        icon: <SafetyCertificateOutlined />,
        status: "finish",
      });
    }

    items.push({
      key: "insured",
      label: "受保人",
      icon: <SafetyCertificateOutlined />,
      status: "finish",
    });

    if (
      orderData.secondInsured &&
      Object.keys(orderData.secondInsured).length > 0
    ) {
      items.push({
        key: "insured2",
        label: "第二受保人",
        icon: <SafetyCertificateOutlined />,
        status: "finish",
      });
    }

    items.push({
      key: "beneficiary",
      label: "受益人",
      icon: <SafetyCertificateOutlined />,
      status: "finish",
    });

    return items;
  }, [orderData]);

  const handleBack = () => {
    navigate("/orders");
  };
  const handleSubmit = (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/orders/${orderId}/risk`);
    // message.info("風險評估問卷功能開發中");
  };

  // 下载所有已签署文件
  const handleDownloadAllSignedFiles = async () => {
    if (!signatureConfig || signatureConfig.length === 0) {
      return;
    }

    // 遍历所有文档并下载
    for (const doc of signatureConfig) {
      if (doc._fileUrl) {
        try {
          // 创建一个临时的 a 标签来触发下载
          const link = document.createElement("a");
          link.href = doc._fileUrl;
          link.download = doc.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // 添加延迟以避免浏览器阻止多个下载
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
          console.error(`下載文件失敗: ${doc.name}`, error);
        }
      }
    }
  };

  // 查看CRA
  const handleViewCRA = async () => {
    setCraLoading(true);
    try {
      const { blob } = await downloadOrderFormPdf({
        formType: "CRA",
        orderId: Number(orderId),
      });

      if (blob) {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("查看CRA失敗:", error);
    } finally {
      setCraLoading(false);
    }
  };
  // Helper to access data based on current sidebar step
  const personData = useMemo(() => {
    switch (currentSidebarStep) {
      case "policyholder":
        return orderData.policyholder;
      case "policyholder2":
        return orderData.secondPolicyholder;
      case "insured":
        return orderData.insured;
      case "insured2":
        return orderData.secondInsured;
      default:
        return {};
    }
  }, [currentSidebarStep, orderData]);

  const renderFormContent = () => {
    if (loading)
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      );

    if (currentLogicalStep === 0) {
      return currentSidebarStep === "beneficiary" ? (
        <BeneficiaryView
          beneficiaries={orderData.beneficiaries || []}
          onBeneficiaryClick={(beneficiary) => {
            setSelectedBeneficiary(beneficiary);
            setBeneficiaryDetailOpen(true);
          }}
        />
      ) : (
        <PersonView
          personData={personData}
          currentSidebarStep={currentSidebarStep}
          formRequiredConfig={formRequiredConfig}
        />
      );
    } else if (currentLogicalStep === 1) {
      if (!orderData?.isFinancialInfoRequired) {
        return (
          <div className={styles["financial-empty-state"]}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="無需填寫財務資料"
            />
          </div>
        );
      }

      return (
        <FinancialView
          financial={orderData.financial || {}}
          currentFinancialStep={currentFinancialStep}
          orderData={orderData}
        />
      );
    } else if (currentLogicalStep === 2) {
      const products = orderData.productPlans || [];
      const activeIndex = parseInt(activeProductKey, 10) || 0;
      const product = products[activeIndex];
      return <ProductView product={product} />;
    } else if (currentLogicalStep === 3) {
      return (
        <AttachmentsView
          attachmentConfig={attachmentConfig}
          selectedCategory={selectedAttachmentCategory}
          onCategoryChange={setSelectedAttachmentCategory}
          loading={loadingAttachments}
          previewOpen={previewOpen}
          previewFile={previewFile}
          onPreviewOpenChange={setPreviewOpen}
          onPreviewFileChange={setPreviewFile}
        />
      );
    } else if (currentLogicalStep === 4) {
      return activeDocKey === "signature-info" ? (
        <SignInfoView
          signInfo={orderData.signInfo}
          signPhotoDisplayUrl={signPhotoDisplayUrl}
          signatureDisplayUrls={signatureDisplayUrls}
          signPhotoPreviewOpen={signPhotoPreviewOpen}
          signPhotoPreviewFile={signPhotoPreviewFile}
          onSignPhotoPreviewOpenChange={setSignPhotoPreviewOpen}
          onSignPhotoPreviewFileChange={setSignPhotoPreviewFile}
          signatureImagePreviewOpen={signatureImagePreviewOpen}
          signatureImagePreviewFile={signatureImagePreviewFile}
          onSignatureImagePreviewOpenChange={setSignatureImagePreviewOpen}
          onSignatureImagePreviewFileChange={setSignatureImagePreviewFile}
        />
      ) : (
        <SignDocumentView
          signatureConfig={signatureConfig}
          activeDocKey={activeDocKey}
          onActiveDocChange={setActiveDocKey}
        />
      );
    } else if (currentLogicalStep === 5) {
      return <RiskAssessmentView orderId={orderId} />;
    }
  };

  return (
    <div className={styles["order-form-container"]}>
      {/* Fixed Header */}
      <div className={styles["sticky-header-container"]}>
        {/* Steps */}
        <div className={styles["steps-wrapper"]}>
          <OrderSteps
            current={currentTopStep}
            steps={updatedTopSteps}
            onChange={handleTopStepClick}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className={styles["main-layout"]}>
        {/* Sidebar - Only show for Step 0 (Order Data) */}
        {currentLogicalStep === 0 && (
          <div className={styles["sidebar"]}>
            {sidebarItems.map((item) => (
              <div
                key={item.key}
                className={`${styles["sidebar-item"]} ${currentSidebarStep === item.key ? styles["active"] : ""}`}
                onClick={() => setCurrentSidebarStep(item.key)}
              >
                <span>{item.label}</span>
                {item.status === "finish" && (
                  <CheckCircleOutlined
                    className={`${styles["status-icon"]} ${styles["status-check"]}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Sidebar - Financial Info (Step 1) */}
        {currentLogicalStep === 1 && (
          <div className={styles["sidebar"]}>
            <div
              className={`${styles["sidebar-item"]} ${styles["sidebar-readonly-item"]}`}
            >
              <div className={styles["sidebar-item-content"]}>
                <span>是否需要填寫財務資料</span>
              </div>
              <div className={styles["sidebar-switch-control"]}>
                <Switch
                  checked={!!orderData?.isFinancialInfoRequired}
                  disabled
                />
              </div>
            </div>

            {orderData?.isFinancialInfoRequired && (
              <div
                className={`${styles["sidebar-item"]} ${currentFinancialStep === "financial-info" ? styles["active"] : ""}`}
                onClick={() => handleFinancialStepClick("financial-info")}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <span>{isZuuMode ? "財務資料" : "基本財務資料"}</span>
                </div>
                <CheckCircleOutlined
                  className={`${styles["status-icon"]} ${styles["status-check"]}`}
                />
              </div>
            )}

            {orderData?.isFinancialInfoRequired && orderData?.fnaCompleted && (
              <div
                className={`${styles["sidebar-item"]} ${currentFinancialStep === "fna" ? styles["active"] : ""}`}
                onClick={() => handleFinancialStepClick("fna")}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <span>{isZuuMode ? "FNA" : "財務需要分析"}</span>
                </div>
                <CheckCircleOutlined
                  className={`${styles["status-icon"]} ${styles["status-check"]}`}
                />
              </div>
            )}
          </div>
        )}

        {/* Sidebar - Product Plans (Step 2) */}
        {currentLogicalStep === 2 &&
          orderData?.productPlans &&
          orderData.productPlans.length > 0 && (
            <div className={styles["product-sidebar"]}>
              {(orderData?.productPlans || []).map(
                (plan: any, index: number) => (
                  <div
                    key={index}
                    className={`${styles["product-sidebar-item"]} ${activeProductKey === String(index) ? styles["product-sidebar-item-active"] : ""}`}
                    onClick={() => setActiveProductKey(String(index))}
                  >
                    <div className={styles["product-sidebar-item-content"]}>
                      <span className={styles["product-sidebar-name"]}>
                        {plan.productName || ` ${index + 1}`}
                      </span>
                      <div style={{ display: "flex" }}>
                        {plan.companyName && (
                          <span className={styles["product-sidebar-sub-info"]}>
                            {plan.companyName}
                          </span>
                        )}{" "}
                        {
                          <span className={styles["product-sidebar-sub-info"]}>
                            {getCountryName(plan.region)}
                          </span>
                        }
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>
          )}

        {/* Sidebar - Signature Documents (Step 4) */}
        {currentLogicalStep === 4 && (
          <div className={styles["sidebar"]}>
            {/* 文档列表 */}
            {signatureConfig.map((doc) => {
              const docKey = doc.key || doc.formType || doc.type || "";
              const isSelected = activeDocKey === docKey;

              return (
                <div
                  key={docKey}
                  onClick={() => setActiveDocKey(docKey)}
                  className={`${styles["sidebar-item"]} ${isSelected ? styles["active"] : ""}`}
                >
                  <div className={styles["sidebar-item-content"]}>
                    {doc.name}
                  </div>
                  <CheckCircleOutlined
                    className={`${styles["status-icon"]} ${styles["status-check"]}`}
                  />
                </div>
              );
            })}

            {/* 簽署信息選項 */}
            <div
              className={`${styles["sidebar-item"]} ${activeDocKey === "signature-info" ? styles["active"] : ""}`}
              onClick={() => setActiveDocKey("signature-info")}
            >
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <span>簽署資料</span>
              </div>
              {orderData?.signInfo && (
                <CheckCircleOutlined
                  className={`${styles["status-icon"]} ${styles["status-check"]}`}
                />
              )}
            </div>
          </div>
        )}

        {/* Form Area */}
        <div
          className={styles["form-area"]}
          ref={formAreaRef}
          style={{
            overflowY:
              currentLogicalStep === 4 || currentLogicalStep === 5
                ? "hidden"
                : "auto",
            paddingRight: currentLogicalStep === 5 ? 0 : 16,
          }}
        >
          <div className={styles["form-content"]}>{renderFormContent()}</div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles["footer-bar"]}>
        <div className={styles["footer-left"]}>
          <CustomButton
            bgColor="#fff"
            color="#666"
            style={{ border: "1px solid #d9d9d9" }}
            height={35}
            onClick={handleBack}
          >
            返回列表
          </CustomButton>
        </div>
        <div className={styles["footer-right"]}>
          {currentTopStep === 4 && (
            <CustomButton height={35} onClick={handleDownloadAllSignedFiles}>
              下載所有已簽署文件
            </CustomButton>
          )}
          {currentTopStep < visibleSteps.length - 1 && (
            <CustomButton
              height={35}
              onClick={() => {
                if (currentLogicalStep === 0) {
                  const currentIndex = sidebarItems.findIndex(
                    (item) => item.key === currentSidebarStep,
                  );
                  if (currentIndex < sidebarItems.length - 1) {
                    setCurrentSidebarStep(sidebarItems[currentIndex + 1].key);
                  } else {
                    handleTopStepClick(currentTopStep + 1);
                  }
                } else if (currentLogicalStep === 1) {
                  if (
                    orderData?.fnaCompleted &&
                    currentFinancialStep === "financial-info"
                  ) {
                    handleFinancialStepClick("fna");
                  } else {
                    handleTopStepClick(currentTopStep + 1);
                  }
                } else if (currentLogicalStep === 2) {
                  const products = orderData?.productPlans || [];
                  const currentIndex = parseInt(activeProductKey, 10) || 0;
                  if (currentIndex < products.length - 1) {
                    setActiveProductKey(String(currentIndex + 1));
                  } else {
                    handleTopStepClick(currentTopStep + 1);
                  }
                } else if (currentLogicalStep === 4) {
                  const allItems = [
                    ...signatureConfig.map(
                      (doc) => doc.key || doc.formType || doc.type || "",
                    ),
                    "signature-info",
                  ];
                  const currentIndex = allItems.findIndex(
                    (key) => key === activeDocKey,
                  );

                  if (currentIndex < allItems.length - 1) {
                    setActiveDocKey(allItems[currentIndex + 1]);
                  } else {
                    handleTopStepClick(currentTopStep + 1);
                  }
                } else {
                  handleTopStepClick(currentTopStep + 1);
                }
              }}
            >
              下一步
            </CustomButton>
          )}
          {currentTopStep === 5 &&
            !(
              orderData?.riskAssessmentAuditStatus === "PENDING_SUBMISSION" ||
              orderData?.riskAssessmentAuditStatus === "INITIAL"
            ) && (
              <CustomButton
                height={35}
                onClick={handleViewCRA}
                loading={craLoading}
              >
                查看CRA
              </CustomButton>
            )}
          {currentTopStep === 5 &&
            orderData?.orderStatus === OrderStatus.OrderAudit && (
              <CustomButton
                height={35}
                onClick={(e) => handleSubmit(orderData?.orderId || "", e)}
              >
                修改風險評估文件
              </CustomButton>
            )}
        </div>
      </div>

      {/* Beneficiary Detail Modal */}
      <BeneficiaryDetailModal
        open={beneficiaryDetailOpen}
        beneficiary={selectedBeneficiary}
        onClose={() => setBeneficiaryDetailOpen(false)}
      />
    </div>
  );
}
